import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useDeliveryNotes } from '../hooks/useDeliveryNotes';
import { DeliveryNote, DeliveryNoteStatus } from '../types/deliveryNote';
import CommonPageHeader from '../components/common/CommonPageHeader';
import SearchBar from '../components/common/SearchBar';
import DeliveryNoteList from '../components/delivery-notes/DeliveryNoteList';
import DeliveryNoteView from '../components/delivery-notes/DeliveryNoteView';
import DeliveryNoteForm from '../components/delivery-notes/DeliveryNoteForm';
import LoadingScreen from '../components/common/LoadingScreen';
import { 
  PlusIcon, 
  TruckIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const DeliveryNotes: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const {
    deliveryNotes,
    loading,
    updateDeliveryNoteStatus,
    updateDeliveryNote,
    deleteDeliveryNote
  } = useDeliveryNotes();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<DeliveryNoteStatus | 'all'>('all');
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState<DeliveryNote | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Jogosultság ellenőrzés
  if (!hasAccess('quotations')) { // DeliveryNotes is part of quotations module
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.errors.loadingFailed')}</div>
      </div>
    );
  }

  // Szűrés
  const filteredDeliveryNotes = deliveryNotes.filter(deliveryNote => {
    const matchesSearch = !searchTerm || 
      deliveryNote.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deliveryNote.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deliveryNote.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (deliveryNote.trackingNumber && deliveryNote.trackingNumber.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = selectedStatus === 'all' || deliveryNote.status === selectedStatus;
    
    return matchesSearch && matchesStatus;
  });

  const handleStatusChange = async (deliveryNoteId: string, status: DeliveryNoteStatus) => {
    const success = await updateDeliveryNoteStatus(deliveryNoteId, status);
    if (success) {
      toast.success(t('deliveryNotes.messages.statusUpdated'));
    }
  };

  const handleEditClick = (deliveryNote: DeliveryNote) => {
    setSelectedDeliveryNote(deliveryNote);
    setIsEditOpen(true);
  };

  const handleDeleteClick = (deliveryNote: DeliveryNote) => {
    if (window.confirm(t('deliveryNotes.messages.confirmDelete'))) {
      deleteDeliveryNote(deliveryNote.id);
    }
  };

  const handleViewClick = (deliveryNote: DeliveryNote) => {
    setSelectedDeliveryNote(deliveryNote);
    setIsViewOpen(true);
  };

  const handleSaveDeliveryNote = async (deliveryNoteId: string, data: any) => {
    const success = await updateDeliveryNote(deliveryNoteId, data);
    if (success) {
      toast.success(t('deliveryNotes.messages.deliveryNoteUpdated'));
    }
  };

  const handleCloseView = () => {
    setIsViewOpen(false);
    setSelectedDeliveryNote(null);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setSelectedDeliveryNote(null);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      <CommonPageHeader
        title={t('deliveryNotes.title')}
        subtitle={t('deliveryNotes.subtitle')}
        icon={TruckIcon}
      >
        <button
          onClick={() => toast(t('deliveryNotes.list.createNewSoon'), { icon: '🚧' })}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>{t('deliveryNotes.list.createNew')}</span>
        </button>
      </CommonPageHeader>

      {/* Keresés és szűrés */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Keresés */}
          <div className="flex-1">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('deliveryNotes.list.searchPlaceholder')}
            />
          </div>

          {/* Státusz szűrő */}
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as DeliveryNoteStatus | 'all')}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            >
              <option value="all">{t('deliveryNotes.filter.all')}</option>
              <option value="draft">{t('deliveryNotes.filter.draft')}</option>
              <option value="pending">{t('deliveryNotes.filter.pending')}</option>
              <option value="shipped">{t('deliveryNotes.filter.shipped')}</option>
              <option value="delivered">{t('deliveryNotes.filter.delivered')}</option>
              <option value="confirmed">{t('deliveryNotes.filter.confirmed')}</option>
              <option value="cancelled">{t('deliveryNotes.filter.cancelled')}</option>
            </select>
          </div>

          {/* Frissítés gomb */}
          <button
            onClick={() => window.location.reload()}
            className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            {t('common.actionsObject.refresh')}
          </button>
        </div>

        {/* Statisztikák */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {filteredDeliveryNotes.length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{t('deliveryNotes.stats.total')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {filteredDeliveryNotes.filter(d => d.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{t('deliveryNotes.stats.pending')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {filteredDeliveryNotes.filter(d => d.status === 'shipped').length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{t('deliveryNotes.stats.shipped')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {filteredDeliveryNotes.filter(d => d.status === 'delivered').length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{t('deliveryNotes.stats.delivered')}</div>
          </div>
        </div>
      </div>

      {/* Szállítólevelek lista */}
      <DeliveryNoteList
        deliveryNotes={filteredDeliveryNotes}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        onViewClick={handleViewClick}
        onStatusChange={handleStatusChange}
        loading={loading}
      />

      {/* View Modal */}
      <DeliveryNoteView
        deliveryNote={selectedDeliveryNote}
        isOpen={isViewOpen}
        onClose={handleCloseView}
      />

      {/* Edit Modal */}
      <DeliveryNoteForm
        deliveryNote={selectedDeliveryNote}
        isOpen={isEditOpen}
        onClose={handleCloseEdit}
        onSave={handleSaveDeliveryNote}
      />
    </div>
  );
};

export default DeliveryNotes; 